import { useQuery } from '@tanstack/react-query'
import { ApiFetchError, apiGet, apiPut } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import type { CellsCell, Project, TeamMember } from '../../lib/types'

// Lo que comparten el panel de Personas (MembersModal) y la sección "Célula y
// personas" del modal de editar (MetaModal): el catálogo, cómo se resuelve la
// membresía y el PUT. Vive aparte para que los dos no se escriban cada uno su
// versión — la del panel ya existía y la del modal de editar nació copiándola.
//
// D-16: las células son de Cells y aquí se LEEN. Asignar un proyecto a una célula
// es un dato del proyecto en camp (`cellIds`); nada de esto escribe en Cells.

/** Roster y células con las MISMAS llaves y formas que el resto de la app ({ members } / { cells }). */
export function useCatalogoMiembros() {
  const teamQ = useQuery({
    queryKey: qk.team,
    queryFn: () => apiGet<{ members: TeamMember[] }>('/api/team'),
  })
  const cellsQ = useQuery({
    queryKey: qk.cells,
    queryFn: () => apiGet<{ cells: CellsCell[] }>('/api/cells'),
  })
  return { teamQ, cellsQ }
}

/**
 * Espejo de resolveMembers (api/src/services/access.ts): directos ∪ gente de las
 * células, ordenado. Sirve para PREVISUALIZAR en el modal quién queda en el
 * proyecto antes de guardar; la lista de verdad la recalcula el server.
 */
export function resolverMiembros(usersAssigned: string[], cellIds: string[], cells: CellsCell[]): string[] {
  const set = new Set(usersAssigned.map((e) => e.toLowerCase()))
  for (const c of cells) if (cellIds.includes(c.cellId)) for (const e of c.usersAssigned) set.add(e.toLowerCase())
  return [...set].sort()
}

/** Por qué alguien está en el proyecto: directo y/o por qué célula(s). */
export function origenDe(
  email: string,
  borrador: { usersAssigned: string[]; cellIds: string[] },
  cells: CellsCell[],
): { directo: boolean; celulas: string[] } {
  return {
    directo: borrador.usersAssigned.includes(email),
    celulas: cells
      .filter((c) => borrador.cellIds.includes(c.cellId) && c.usersAssigned.includes(email))
      .map((c) => c.cellName),
  }
}

/**
 * Quién puede tocar personas y células: lo MISMO que exige PUT /members —owner o
 * admin, de casa (admin/miembro, el isGuest del server) y con la clave
 * PROYECTOS.MIEMBROS.EDITAR—. Un botón que rebota con 403 es peor que no tenerlo.
 */
/**
 * De casa = rol de sistema admin o miembro, el MISMO cierre que ROLES_DE_CASA del
 * server (services/access.ts): un rol de datos creado en /admin es externo mientras
 * nadie diga lo contrario.
 */
export const esDeCasa = (me: { role: string } | null | undefined): boolean =>
  me?.role === 'admin' || me?.role === 'miembro'

export function puedeEditarMiembros(
  me: { email: string; role: string } | null | undefined,
  project: Pick<Project, 'ownerEmail'>,
  can: (key: string) => boolean,
): boolean {
  if (!me) return false
  const deCasa = esDeCasa(me)
  const manda = me.role === 'admin' || me.email === project.ownerEmail
  return deCasa && manda && can('PROYECTOS.MIEMBROS.EDITAR')
}

/**
 * PUT /projects/:pid/members. Cada campo es opcional (el que no va se conserva) y
 * `ifMatch` también: el modal de editar lo manda porque encadena su PATCH con el
 * etag que esta respuesta devuelve; el panel de Personas no lo necesita.
 */
export function guardarMiembros(
  pid: string,
  cambios: { usersAssigned?: string[]; cellIds?: string[] },
  ifMatch?: string,
) {
  return apiPut<{ project: Project; etag?: string }>(`/api/projects/${pid}/members`, cambios, ifMatch)
}

export const mismoConjunto = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((x) => b.includes(x))

/**
 * Suma UNA persona directa al proyecto (el "¿Agregarla?" del aviso de menciones).
 *
 * PUT /members REEMPLAZA la lista entera de `usersAssigned`, así que armarla con el
 * proyecto que la pantalla tiene en caché podía sacar a alguien que otra pestaña
 * acababa de meter. Por eso se lee el proyecto FRESCO y se manda con su etag
 * (If-Match): si alguien lo tocó entre la lectura y el PUT, el 412 hace releer y
 * reintentar una vez. `cellIds` no viaja: el que no va se conserva tal cual.
 *
 * Si la persona ya es miembro (la agregó otra pestaña o entró por una célula) no se
 * escribe nada: se devuelve el proyecto tal como está.
 *
 * Dentro de la MISMA pestaña los altas van en fila (dos avisos, dos clics seguidos):
 * en paralelo, el segundo PUT chocaba siempre con el etag que el primero acababa de
 * cambiar y gastaba su único reintento en eso.
 */
let filaDeAltas: Promise<unknown> = Promise.resolve()

export function agregarAlProyecto(pid: string, email: string): Promise<{ project: Project; etag?: string }> {
  const correo = email.trim().toLowerCase()
  const intentar = async () => {
    const actual = await apiGet<{ project: Project; etag: string }>(`/api/projects/${pid}`)
    if (actual.project.memberEmailsResolved.includes(correo)) return actual
    return guardarMiembros(pid, { usersAssigned: [...actual.project.usersAssigned, correo] }, actual.etag)
  }
  const turno = filaDeAltas.then(async () => {
    try {
      return await intentar()
    } catch (err) {
      if (err instanceof ApiFetchError && err.status === 412) return intentar()
      throw err
    }
  })
  // Un fallo no rompe la fila: el siguiente turno corre igual.
  filaDeAltas = turno.catch(() => undefined)
  return turno
}
